import { browserName, isMobile, isTablet, osName } from 'react-device-detect';

import { EEventType } from '@/components/eventType';
import { getActiveProgressText } from '@/components/ProgressBar';
import { store } from '@/context/store';
import {
    getSessionId,
    INACTIVITY_WINDOW_MS,
    nextSequence,
} from '@/utils/localStorage';

import {
    CalculatorEstimateInput,
    getTrackingCalculatorEstimate,
    recordCalculatorEstimate,
} from './calculatorEstimate';
import { CustomerRecordInterface } from './customerRecordType';
import { getEmbedPage } from './embedPage';
import { extractScreen, extractSurface } from './helper';
import { getListingIdentity } from './listingIdentity';
import {
    flushOnScreenChange,
    flushTrackingBuffer,
    queueTrackingEvent,
    refreshPendingSession,
    registerBeforeFinalFlush,
} from './trackingBuffer';
import {
    TrackingEntryMode,
    TrackingEvent,
    TrackingFieldStatus,
    TrackingOutcomeType,
    TrackingRequest,
    TrackingServiceResult,
    TrackingSurface,
} from './TrackingRequest';
import { getSource } from './trackingSource';
import { getTrackingVehicle } from './trackingVehicle';

type SessionFields = Omit<TrackingRequest, 'events'>;

// Rejects a hostile vroomCode in the embed URL before anything is sent.
const GARBAGE_REGEX = new RegExp(
    `^(?=.*(?:<\\w+|alert|script|union|select|passwd|system\\.ini|\\.owasp\\.org|win\\.ini|response\\.write|set-cookie|print|cat |etc|type|sleep|timeout|src=x|onerror|prompt|;|{|%|\\$\\{|www\\.google\\.com|\\d{4,})).*$`
);

// Session-scoped state.
//
// entryMode only ever moves towards a more specific value. The first screen
// mounts before the return-user modal renders and before any login completes,
// so the earliest thing we can say is 'new' — and it has to be possible for the
// truth to arrive afterwards without a later page view knocking it back down.
const ENTRY_MODE_RANK: Record<TrackingEntryMode, number> = {
    new: 0,
    recognised: 1,
    loggedIn: 2,
    signedUp: 2,
};

let entryMode: TrackingEntryMode | undefined;
let isResumed: boolean | undefined;
let resumedAtStage: string | undefined;
let loginFromScreen: string | undefined;
let hasRecordedWidgetRender = false;
let hasRecordedEngagement = false;
let hasRecordedOutcome = false;
let hasRecordedSessionEnd = false;
let lastScreen: string | undefined;
let lastField: string | undefined;

function upgradeEntryMode(mode: TrackingEntryMode): void {
    if (!entryMode || ENTRY_MODE_RANK[mode] > ENTRY_MODE_RANK[entryMode]) {
        entryMode = mode;
    }
}

// Staff demonstrating the tool on a live partner site are otherwise
// indistinguishable from real applicants: source only marks localhost, dev and
// uat, and a dealer showing a customer the widget is on production.
//
// Marked by an `internal=true` URL parameter on the staff embed, and remembered
// for the session so it survives navigation within the widget.
const INTERNAL_STORAGE_KEY = 'youxapply-internal';

function isInternalTraffic(): boolean {
    try {
        const param = new URLSearchParams(window.location.search).get('internal');

        if (param === 'true' || param === '1') {
            sessionStorage.setItem(INTERNAL_STORAGE_KEY, 'true');
            return true;
        }

        // sessionStorage, not localStorage: a staff member should not be marked
        // internal forever on a shared machine a customer might later use.
        return sessionStorage.getItem(INTERNAL_STORAGE_KEY) === 'true';
    } catch (error) {
        return false;
    }
}

// The device's own IANA zone, eg 'Australia/Brisbane'. Available for every
// session including the ones that leave immediately, which is the population an
// entered address can never describe.
// Mobile, tablet or desktop. Derived from the booleans rather than the library's
// deviceType, which reports desktop as 'browser' and is undefined often enough to
// be awkward.
//
// Deliberately not the phone vendor or model: those are available and would add
// a lot of fingerprinting surface for very little that anyone would act on. The
// question worth answering is whether a small screen converts worse, and this
// answers it.
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (isTablet) return 'tablet';
    if (isMobile) return 'mobile';
    return 'desktop';
}

function getTimeZone(): string | undefined {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch (error) {
        return undefined;
    }
}

function getStatus(): string {
    const state = store.getState();
    if (state.borrowerReducer.application.isDeclined) return 'Declined';
    if (state.borrowerReducer.application.isSubmitted) return 'Submitted';
    if (state.borrowerReducer.application.isPreApproved) return 'Pre Approved';
    return 'In Progress';
}

// widgetId is the first URL path segment, so a codeless /iframe/:id embed puts
// the literal route prefix 'iframe' here — a placeholder, not a partner code.
// The other two are the code-snippet and unknown-partner defaults.
const VROOM_CODE_PLACEHOLDERS = new Set(['iframe', 'codesnippet', 'UNKNOWN']);

function getVroomCode(widgetId: string | null | undefined): string | undefined {
    if (!widgetId || VROOM_CODE_PLACEHOLDERS.has(widgetId)) return undefined;
    return widgetId;
}

// Everything that describes the session rather than the event. Rebuilt on every
// send so late-arriving values — the application id, the mobile number — travel
// with the next batch and land on the document earlier events already created.
function buildSessionFields(): SessionFields | null {
    const state = store.getState();
    const partner = state.displayReducer.partner;

    if (GARBAGE_REGEX.test(partner.id)) {
        console.warn('Potential security threat detected. Aborting operation.');
        return null;
    }

    const sessionId = getSessionId();

    return {
        sessionId,
        partnerId: partner.partnerId ?? '',
        storeId: partner.partnershipConfigId ?? '',
        brandId: partner.brandId ?? '',
        organisationId: partner.organisationId ?? '',

        // The template carries the journey configuration, and configVersion says
        // which revision of it this session ran under — so a screen appearing or
        // disappearing from the funnel is explained rather than investigated.
        templateId: partner.templateId,
        configVersion: partner.configVersion,

        // The whole Apply -> lead -> Manage chain hangs off this one field.
        applicationId: state.borrowerReducer.application.applicationId,

        // Sent raw, hashed server side, never stored in the clear.
        mobilePhone: state.borrowerReducer.personalDetails.mobilePhone,

        // The vroom code from the URL, eg M6307. partnerId above is the Mongo
        // _id, which nobody recognises — without this a report can only identify
        // a partner by an opaque id. Placeholders are withheld: an absent code
        // is honest, a literal 'iframe' is 98k documents a day of noise.
        vroomCode: getVroomCode(partner.widgetId),

        userFlow: partner.userFlow,
        source: getSource(),
        isInternal: isInternalTraffic(),
        timeZone: getTimeZone(),

        // Which page of the partner's site the widget is embedded on, how it is
        // embedded, and how that page was determined.
        ...getEmbedPage(),

        // Which vehicle listing, from the identifiers already on the iframe src.
        ...getListingIdentity(),

        // The vehicle body the applicant resolved in the journey, if a Glass's
        // Guide lookup has landed by the time this batch goes out. Rebuilt on
        // every emit, so a later selection overwrites an earlier one — the API
        // $sets this field, so the last one sent wins.
        ...getTrackingVehicle(),

        // The last repayment estimate the quick calculator showed, if any.
        // Rebuilt on every emit like the vehicle body above, so the figure
        // the applicant last saw rides out whether they continued or left.
        ...getTrackingCalculatorEstimate(sessionId),

        entryMode,
        isResumed,
        resumedAtStage,
        loginFromScreen,

        userDevice: {
            deviceType: getDeviceType(),
            browser: browserName,
            // Previously populated from browserVersion, so the OS column held a
            // browser version number and was unusable.
            os: osName,
            // The viewport, not the physical screen.
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
        },
    };
}

// A session that goes quiet never records an end: nothing fires visibilitychange
// on a tab simply left open, so the timeout has to be watched from here. The
// margin keeps the end event inside the session window — at the full thirty
// minutes the id has expired and the event would mint a new session and attach
// the end to that one instead.
const SESSION_END_MARGIN_MS = 60 * 1000;

let sessionIdleTimer: ReturnType<typeof setTimeout> | null = null;

function armSessionIdleTimer(): void {
    if (sessionIdleTimer) clearTimeout(sessionIdleTimer);

    sessionIdleTimer = setTimeout(() => {
        trackSessionEnded('inactivity', lastField);
        flushTrackingBuffer();
    }, INACTIVITY_WINDOW_MS - SESSION_END_MARGIN_MS);
}

function emit(event: TrackingEvent): void {
    // A locked widget reports nothing at all. Both locked screens are
    // rendered without the journey behind them, so any event from one is a
    // session that could never convert — counted in the funnel it would sit
    // in whichever step that brand opens on and drag its conversion rate
    // down for as long as the lock is on. The holding screen already emits
    // nothing by construction; the frozen first screen would emit a screen
    // view, a widget render and a screen exit through DisplayComponent, so
    // the guard belongs here rather than on any one screen — it is an
    // ordinary journey screen that has no idea it is frozen.
    if (store.getState().displayReducer.partner.widgetLocked === true) return;

    const session = buildSessionFields();
    if (!session) return;

    // Stamped here, at the moment the event happens, rather than on receipt.
    // This is what the path is reconstructed from — see nextSequence.
    queueTrackingEvent(session, { ...event, seq: nextSequence() });

    // Every recorded event is activity, so every one pushes the inactivity
    // deadline back — mirroring the sliding session window itself.
    armSessionIdleTimer();
}

// Context snapshotted onto every event so a journey's progression is readable
// from one document without joining anything.
function eventContext(
    screen?: string,
    surface?: TrackingSurface,
): Pick<TrackingEvent, 'screen' | 'surface' | 'parentScreen' | 'status' | 'financeType' | 'vehicleCondition'> {
    return {
        screen,
        surface,
        parentScreen: getActiveProgressText(),
        status: getStatus(),
        financeType: store.getState().borrowerReducer.loan.financeType,
        vehicleCondition: store.getState().borrowerReducer.vehicle.condition,
    };
}

// ***** Entry and identity *****

// The tool painted. Distinct from engagement: an impression is not a start, and
// counting them as one thing removes the denominator from every drop-off figure.
export function trackWidgetRendered(mode: TrackingEntryMode, resumed = false): void {
    upgradeEntryMode(mode);
    if (resumed) {
        isResumed = true;

        // Where in the journey they picked it back up. The resumed screen has
        // rendered by the time the return-user modal mounts, so lastScreen is
        // already the resumption point; redux covers a resume signal that
        // arrives before any screen has reported in.
        if (!resumedAtStage) {
            resumedAtStage =
                lastScreen ?? store.getState().displayReducer.display;
        }
    }

    // The event itself fires once; the entry mode above can still be refined by
    // a later signal and travels with whatever batch goes out next.
    if (hasRecordedWidgetRender) return;
    hasRecordedWidgetRender = true;

    emit({ type: 'widgetRendered', ...eventContext() });
}

// The first genuine interaction — a field focused, a slider moved, a primary
// call to action pressed. This is what "started using the tool" means.
export function trackEngagement(trigger: string): void {
    if (hasRecordedEngagement) return;
    hasRecordedEngagement = true;

    emit({ type: 'engaged', trigger, ...eventContext(lastScreen) });
}

// Where in the journey they chose to log in. The login control sits on every
// screen, so a cluster on one screen means applicants are leaving to resume
// later and that screen is the reason.
export function trackLogin(mode: Extract<TrackingEntryMode, 'loggedIn' | 'signedUp'>): void {
    loginFromScreen = lastScreen;
    upgradeEntryMode(mode);

    emit({ type: 'engaged', trigger: mode, ...eventContext(lastScreen) });
}

// ***** Screens *****

// Takes the element id, not a screen name, so the extraction rule lives in one
// place and screenExit records the same value that visibleOnScreen does.
export function trackScreenExit(elementId: string, dwellMs: number): void {
    const screen = extractScreen(elementId);
    if (!screen) return;

    emit({
        type: 'screenExit',
        ...eventContext(screen, extractSurface(elementId)),
        screen,
        dwellMs: Math.round(dwellMs),
    });
}

// ***** Fields *****

// Records that a field was used, never what was typed in it. editCount is the
// friction signal: a field revised many times before blur is a field that is
// hard to answer.
export function trackFieldInteraction(
    field: string,
    fieldStatus: TrackingFieldStatus,
    dwellMs?: number,
    editCount?: number
): void {
    // The last field touched is where an abandoning applicant actually
    // stopped, which is finer-grained than the screen they stopped on.
    lastField = field;

    emit({
        type: 'fieldInteraction',
        field,
        fieldStatus,
        dwellMs: dwellMs === undefined ? undefined : Math.round(dwellMs),
        editCount,
        ...eventContext(lastScreen),
    });
}

export function trackValidationFailed(field: string, errorCode?: string): void {
    emit({ type: 'validationFailed', field, errorCode, ...eventContext(lastScreen) });
}

// Radios, checkboxes, sliders, dropdowns and date pickers commit on change rather
// than on blur, so there is no focus-to-blur window the way a text box has one.
// Dwell is measured from when the control was focused instead, which is why the
// components call markFieldFocus on focus — without it these fields reported no
// time at all and the friction table had a permanently empty column for every
// control that was not a text box.
const selectionRevisions = new Map<string, number>();
const focusedAt = new Map<string, number>();

function now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function markFieldFocus(field: string | undefined): void {
    if (!field) return;

    // Only the first focus counts, so returning to a control mid-answer measures
    // the whole time spent on it rather than restarting the clock.
    if (!focusedAt.has(field)) focusedAt.set(field, now());

    trackEngagement(`field:${field}`);
}

export function trackSelection(field: string | undefined): void {
    if (!field) return;

    trackEngagement(`selection:${field}`);
    lastField = field;

    const revisions = (selectionRevisions.get(field) ?? 0) + 1;
    selectionRevisions.set(field, revisions);

    const startedAt = focusedAt.get(field);

    emit({
        type: 'fieldInteraction',
        ...eventContext(lastScreen),
        field,
        fieldStatus: 'completed',
        dwellMs: startedAt === undefined ? undefined : Math.round(now() - startedAt),
        editCount: revisions,
    });
}

// A document upload is the one field type where the attempt and the result are
// different facts: an applicant who tried three times and failed has not
// "skipped" the field. Records neither filename nor content.
export function trackDocumentUpload(
    field: string | undefined,
    result: TrackingServiceResult,
    durationMs?: number,
    errorCode?: string
): void {
    if (!field) return;

    emit({
        type: 'serviceResult',
        ...eventContext(lastScreen),
        service: `upload:${field}`,
        result,
        durationMs: durationMs === undefined ? undefined : Math.round(durationMs),
        errorCode,
    });
}

// ***** Services and outcomes *****

// durationMs matters as much as the result: a service that takes forty seconds
// loses applicants without ever returning an error.
export function trackServiceResult(
    service: string,
    result: TrackingServiceResult,
    durationMs?: number,
    errorCode?: string
): void {
    emit({
        type: 'serviceResult',
        service,
        result,
        durationMs: durationMs === undefined ? undefined : Math.round(durationMs),
        errorCode,
        ...eventContext(lastScreen),
    });
}

// Wraps a service call so its result and duration are recorded without changing
// its behaviour: the value is returned and any error rethrown exactly as before.
//
// Timeouts are distinguished from failures because they mean something different
// operationally — a service that is slow but eventually answers loses applicants
// silently, whereas one that errors at least surfaces.
export async function trackedServiceCall<T>(
    service: string,
    call: () => Promise<T>
): Promise<T> {
    const startedAt =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
    const elapsed = (): number =>
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
        startedAt;

    // Recording the outcome must never change the outcome. The record runs
    // after the call has already resolved or rejected, and a throw inside the
    // tracking path (an unexpected store shape, a storage exception) would
    // otherwise turn a successful quote or lookup into a rejection — and the
    // quick quote and three Next handlers await this wrapper directly.
    const record = (result: TrackingServiceResult, code?: string): void => {
        try {
            trackServiceResult(service, result, elapsed(), code);
        } catch {
            // Telemetry only. Swallowed deliberately.
        }
    };

    let result: T;
    try {
        result = await call();
    } catch (error) {
        const code = (error as { code?: string })?.code;
        const timedOut = code === 'ECONNABORTED' || code === 'ETIMEDOUT';
        record(timedOut ? 'timeout' : 'failure', code);
        throw error;
    }

    record('success');
    return result;
}

// A terminal state. systemFailed is deliberately separate from abandoned: if an
// applicant stops because a service of ours fell over, that is an outage, not a
// drop-off, and counting them together sends the fix to the wrong team.
export function trackOutcome(outcomeType: TrackingOutcomeType, reason?: string): void {
    if (hasRecordedOutcome) return;
    hasRecordedOutcome = true;

    emit({ type: 'outcome', outcomeType, reason, ...eventContext(lastScreen) });

    // A terminal state is worth getting out of the buffer immediately: the
    // applicant may well close the tab on this screen.
    flushOnScreenChange();
}

// Screens that mean the journey ended, keyed by the extracted screen name.
//
// Deriving the outcome from the screen keeps it in one place rather than
// scattered across a dozen components, and means a new terminal screen is a
// one-line addition here rather than an edit that is easy to forget.
//
// servicingSuccess is deliberately absent: it sits mid-journey at roughly
// 55% progress, and the first outcome wins — mapping it to 'approved' froze
// every session that later declined as an approval. Passing affordability is
// not an outcome; a session that stops there and never returns abandoned.
//
// 'novated' (NovatedApproved's baseId) currently renders IndicativelyApproved
// and so records via 'indicativelyApproved'; the mapping is here so restoring
// its own DisplayComponent cannot silently drop novated approvals.
const TERMINAL_SCREEN_OUTCOMES: Record<string, TrackingOutcomeType> = {
    indicativelyApproved: 'approved',
    novated: 'approved',
    TestDriveReady: 'approved',
    indicativelyDeclined: 'declined',
    IndicativelyReferred: 'referred',
    TestDriveIndicativelyReferred: 'referred',
    servicingFailure: 'servicingFailed',
    // The check could not be run, so this is an outage rather than an
    // affordability answer. Left out of this map it would record no outcome
    // at all and later count as a drop-off, which is the exact confusion
    // systemFailed exists to avoid.
    servicingTechnicalFailure: 'systemFailed',
    ineligible: 'ineligible',
    EmploymentIneligible: 'ineligible',
    IncomeIneligible: 'ineligible',
    NoIncomeIneligible: 'ineligible',
};

// The ineligible screens differ only in why, so the screen name is the reason.
function recordOutcomeForScreen(screen: string): void {
    const outcome = TERMINAL_SCREEN_OUTCOMES[screen];
    if (outcome) {
        trackOutcome(outcome, screen);
        return;
    }

    // IndicativelyDeclined renders without a DisplayComponent, so no page view
    // ever fires for it. The declined state is in redux either way.
    if (!hasRecordedOutcome && store.getState().borrowerReducer.application.isDeclined) {
        trackOutcome('declined', 'isDeclined');
    }
}

export function trackSessionEnded(endReason: string, lastField?: string): void {
    if (hasRecordedSessionEnd) return;
    hasRecordedSessionEnd = true;

    emit({ type: 'sessionEnded', endReason, lastScreen, lastField, ...eventContext(lastScreen) });
}

// Queued immediately before the final flush, so the last thing recorded is where
// the applicant actually was when they left. Without this the exit point had to
// be inferred from whichever event happened to be last, and the events that
// matter most are the ones a page hide would otherwise strand in the buffer.
//
// It is not itself an abandonment: a session that ended on a terminal screen
// finished. The distinction is made by whether an outcome was recorded.
registerBeforeFinalFlush(() => {
    trackSessionEnded(hasRecordedOutcome ? 'completed' : 'pageHidden', lastField);
});

// The estimate the quick calculator is showing right now. Recorded as session
// state (see calculatorEstimate.ts) and then pushed into the batch already in
// the buffer: the quote lands a couple of seconds after the input change whose
// event is sitting there, so without this the batch would carry the previous
// figure and the new one would depend on a later event — or on the final flush.
export function trackCalculatorEstimate(input: CalculatorEstimateInput): void {
    // Called from a React effect: a throw here would unmount the widget, and
    // telemetry must never do that.
    try {
        recordCalculatorEstimate(input, getSessionId());

        const session = buildSessionFields();
        if (session) refreshPendingSession(session);
    } catch (error) {
        /* telemetry is best effort */
    }
}

// ***** Page views *****

// The entry point the UI components already call. Only the page view arrives
// here; every other event type has its own exported emitter above.
export async function uniqueIdTransfer(
    customerRecordObject: CustomerRecordInterface
): Promise<void> {
    if (customerRecordObject.eventType !== EEventType.VISIBLE_ON_SCREEN) return;

    const id = customerRecordObject.applicationData?.id;
    const screen = extractScreen(id);
    if (!screen) return;

    // quickCalculator and returnUser are no longer suppressed. They are the
    // entry surface and the resumption signal, and for a partner whose journey
    // starts on the quick calculator, excluding them means no session start is
    // ever recorded and the funnel has no denominator. Filter them at report
    // time instead.
    const surface = extractSurface(id);

    // A new screen means the previous screen's events should not still be
    // sitting in the buffer.
    if (lastScreen && lastScreen !== screen) flushOnScreenChange();
    lastScreen = screen;

    emit({ type: 'visibleOnScreen', ...eventContext(screen, surface), screen });

    // A terminal screen becoming visible is how the journey ended.
    recordOutcomeForScreen(screen);
}
